import { useEffect, useMemo, useState } from "react";
import {
  BookOpenText,
  Building2,
  FileSearch,
  LibraryBig,
  LoaderCircle,
  Search,
  Sparkles,
  UserRound,
} from "lucide-react";
import { AppLink, FeedbackBanner, PageHeader, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  catalogLinkState,
  CatalogPagination,
  formatLegalDate,
} from "../components/LegalCatalogUi.jsx";
import { DoctrineCover } from "../components/DoctrineCover.jsx";
import {
  buildDoctrineSearchUrl,
  doctrineSearchFromUrl,
} from "../lib/legal-catalog-query.js";

const matchLabels = {
  author: "Coincidencia en autor",
  title: "Coincidencia en título",
  content: "Coincidencia en contenido",
  hybrid: "Coincidencia híbrida",
  semantic: "Coincidencia conceptual",
  catalog: "Catálogo",
};

export function DoctrineCatalogPage() {
  const api = useApi();
  const { search: routeSearch, navigate } = useRouter();
  const route = useMemo(() => doctrineSearchFromUrl(routeSearch), [routeSearch]);
  const [formQuery, setFormQuery] = useState(route.query);
  const [coverage, setCoverage] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => setFormQuery(route.query), [route.query]);

  useEffect(() => {
    let active = true;
    api.getDoctrineCoverage()
      .then((data) => active && setCoverage(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.searchDoctrine({ query: route.query, page: route.page })
      .then((data) => {
        if (!active) return;
        if (data.pagination.page > data.pagination.totalPages) {
          navigate(buildDoctrineSearchUrl({
            query: route.query,
            page: data.pagination.totalPages,
          }), { replace: true });
          return;
        }
        setResult(data);
      })
      .catch((requestError) => {
        if (!active) return;
        setResult(null);
        setError(requestError.message);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, navigate, reloadToken, route.page, route.query]);

  const submit = (event) => {
    event.preventDefault();
    const query = formQuery.trim();
    if (query && query.length < 2) {
      setError("Ingresá al menos 2 caracteres para buscar doctrina.");
      return;
    }
    const target = buildDoctrineSearchUrl({ query });
    if (`/doctrina${routeSearch}` === target) {
      setReloadToken((current) => current + 1);
      return;
    }
    navigate(target);
  };

  const goToPage = (page) => navigate(buildDoctrineSearchUrl({
    query: route.query,
    page,
  }));

  return (
    <div className="page legal-catalog-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Doctrina"
        title="Buscar doctrina"
        description="Navegá las obras disponibles o buscá con un solo campo por autor, título o contenido. Las coincidencias bibliográficas aparecen primero."
      />

      <section className="legal-catalog-coverage" aria-label="Cobertura doctrinaria">
        <span><LibraryBig size={15} /> Catálogo doctrinario</span>
        {coverage?.publishers ? (
          <span><Building2 size={15} /> {coverage.publishers} editoriales o fuentes</span>
        ) : null}
        {coverage?.fromDate && coverage?.toDate ? (
          <span>
            <BookOpenText size={15} /> {formatLegalDate(coverage.fromDate)}–{formatLegalDate(coverage.toDate)}
          </span>
        ) : null}
        <strong>{Number(coverage?.total || 0).toLocaleString("es-AR")} obras</strong>
      </section>

      <form className="legal-catalog-search" onSubmit={submit}>
        <label htmlFor="doctrine-query">Autor, título o contenido</label>
        <div className="legal-catalog-search-control">
          <Search size={20} />
          <input
            id="doctrine-query"
            value={formQuery}
            onChange={(event) => {
              setFormQuery(event.target.value);
              setError("");
            }}
            placeholder="Ej. Kemelmajer, derecho de daños o carga probatoria"
            autoFocus
          />
          <button className="primary-button" disabled={loading}>
            {loading ? <LoaderCircle className="spin" size={17} /> : <Search size={17} />}
            {loading ? "Buscando…" : "Buscar"}
          </button>
        </div>
        <p>Dejá el campo vacío para volver a recorrer todo el catálogo.</p>
      </form>

      {error && <FeedbackBanner>{error}</FeedbackBanner>}
      {!loading && result?.warning && <FeedbackBanner>{result.warning}</FeedbackBanner>}

      {loading && (
        <section className="legal-catalog-loading" aria-live="polite">
          <LoaderCircle className="spin" size={22} />
          <span>Consultando el catálogo doctrinario…</span>
        </section>
      )}

      {!loading && result?.items?.length > 0 && (
        <section className="legal-catalog-results" aria-live="polite">
          <header>
            <div>
              <span className="eyebrow">{route.query ? "Resultados priorizados" : "Explorar catálogo"}</span>
              <h2>
                {result.pagination.total.toLocaleString("es-AR")} obra{result.pagination.total === 1 ? "" : "s"}
              </h2>
              <p>{route.query ? "Autor y título tienen prioridad sobre las coincidencias encontradas sólo en el contenido." : "Ordenadas alfabéticamente para recorrer la colección disponible."}</p>
            </div>
            {route.query && (
              <span className={`search-mode-pill is-${result.searchMode}`}>
                {result.searchMode === "hybrid" ? <Sparkles size={14} /> : <Search size={14} />}
                {result.searchMode === "hybrid" ? "Búsqueda híbrida" : "Búsqueda textual"}
              </span>
            )}
          </header>

          <div className="legal-catalog-list">
            {result.items.map((item) => (
              <article className="legal-catalog-card doctrine-catalog-card" key={item.id}>
                <div className="doctrine-catalog-cover-slot">
                  <DoctrineCover
                    api={api}
                    cover={item.cover}
                    title={item.title}
                    authors={item.authors}
                    edition={item.edition}
                    seed={item.id}
                  />
                </div>
                <div className="legal-catalog-card-content">
                  <div className="legal-catalog-card-kicker">
                    <span className={`catalog-match-pill is-${item.matchField}`}>
                      {["hybrid", "semantic"].includes(item.matchField) && <Sparkles size={11} />}
                      {matchLabels[item.matchField] || "Catálogo"}
                    </span>
                    {item.edition && <span>{item.edition}</span>}
                  </div>
                  <AppLink
                    to={item.url}
                    state={catalogLinkState(`/doctrina${routeSearch}`)}
                    className="legal-catalog-title"
                  >
                    {item.title}
                  </AppLink>
                  {item.subtitle && <p className="legal-catalog-subtitle">{item.subtitle}</p>}
                  <div className="legal-catalog-meta">
                    <span><UserRound size={14} /> {item.authors.join(", ") || "Autor no informado"}</span>
                    {item.publisher && <span><Building2 size={14} /> {item.publisher}</span>}
                    {item.pageCount > 0 && <span><BookOpenText size={14} /> {item.pageCount} páginas</span>}
                  </div>
                  {route.query && item.passage && (
                    <blockquote className="legal-catalog-passage">
                      {item.passage}
                      {item.passageStartPage && (
                        <small>p. {item.passageStartPage}{item.passageEndPage !== item.passageStartPage ? `–${item.passageEndPage}` : ""}</small>
                      )}
                      <AppLink
                        to={item.passageUrl}
                        state={catalogLinkState(`/doctrina${routeSearch}`)}
                        className="legal-passage-source"
                      >
                        Abrir fuente Tribunalea
                      </AppLink>
                    </blockquote>
                  )}
                  <AppLink
                    to={item.url}
                    state={catalogLinkState(`/doctrina${routeSearch}`)}
                    className="secondary-button catalog-open-button"
                  >
                    Abrir obra
                  </AppLink>
                </div>
              </article>
            ))}
          </div>
          <CatalogPagination
            pagination={result.pagination}
            onPage={goToPage}
            itemLabel="obras"
            ariaLabel="Páginas del catálogo de doctrina"
          />
        </section>
      )}

      {!loading && !error && result?.items?.length === 0 && (
        <section className="legal-catalog-empty">
          <FileSearch size={26} />
          <h2>No encontramos obras</h2>
          <p>Probá con el apellido del autor, menos palabras del título o un concepto más general.</p>
        </section>
      )}
    </div>
  );
}
